import { LayerComponentProps } from '@lidojs/design-core';
import React, { useEffect, useMemo } from 'react';
import { useEditor, useSelectedLayers } from '../hooks';
import {
  FrameLayerProps,
  ImageLayerProps,
  LineLayerProps,
  RootLayerProps,
  ShapeLayerProps,
  SvgLayerProps,
  TextLayerProps,
  VideoLayerProps,
} from '../layers';

import { CarouselLayerProps } from '../layers/CarouselLayer';
import { Layer } from '../types';
import {
  isCarouselLayer,
  isFrameLayer,
  isGroupLayer,
  isImageLayer,
  isLineLayer,
  isRootLayer,
  isShapeLayer,
  isSvgLayer,
  isTextLayer,
  isVideoLayer,
} from '../ultils/layer/layers';
import { AnimationSettings } from './AnimationSettings';
import { CarouselSettings } from './CarouselSettings';
import CommonSettings from './CommonSettings';
import FrameSettings from './FrameSettings';
import HyperlinkSettings from './HyperlinkSettings';
import ImageSettings from './ImageSettings';
import ImpressionTagSettings from './ImpressionTagSettings';
import LineSettings from './LineSettings';
import { PremiumSettings } from './PremiumSettings';
import RootSettings from './RootSettings';
import ShapeSettings from './ShapeSettings';
import SvgSettings from './SvgSetting';
import TextSettings from './TextSettings';
import VideoSettings from './VideoSettings';

const LayerSettings = () => {
  const { selectedLayers, selectedLayerIds, layerIdList } = useSelectedLayers();
  const { actions, query, activePage, sidebar, isPremium ,isPageLocked } = useEditor(
    (state) => ({
      sidebar: state.sidebar,
      activePage: state.activePage,
      isPremium: state.pages[state.activePage] && state.pages[state.activePage].layers.ROOT.data?.isPremium,
      isPageLocked:
        state.pages[state.activePage] &&
        state.pages[state.activePage].layers.ROOT.data.locked,
    })
  );

  const {
    rootLayer,
    textLayers,
    lineLayers,
    shapeLayers,
    svgLayers,
    frameLayers,
    imageLayers,
    carouselLayers,
    videoLayers
  } = useMemo(() => {
    return selectedLayers.reduce(
      (acc, layer) => {
        if (layer.data.locked && !isPremium) {
          return acc;
        }

        const addingToList = (layer: Layer<LayerComponentProps>) => {
          if (isSvgLayer(layer)) {
            acc.svgLayers.push(layer);
          } else if (isTextLayer(layer)) {
            acc.textLayers.push(layer);
          } else if (isShapeLayer(layer)) {
            acc.shapeLayers.push(layer);
          } else if (isFrameLayer(layer)) {
            acc.frameLayers.push(layer);
          } else if (isLineLayer(layer)) {
            acc.lineLayers.push(layer);
          } else if (isImageLayer(layer)) {
            acc.imageLayers.push(layer);
          } else if (isCarouselLayer(layer)) {
            acc.carouselLayers.push(layer);
          } else if (isVideoLayer(layer)) {
            acc.videoLayers.push(layer);
          }
        };

        if (isRootLayer(layer)) {
          acc.rootLayer = layer;
        } else if (isGroupLayer(layer)) {
          const childList = query.getChildLayers(activePage, layer.id);
          childList.forEach((child) => {
            addingToList(child);
          });
        } else {
          addingToList(layer);
        }
        return acc;
      },
      {
        textLayers: [],
        lineLayers: [],
        shapeLayers: [],
        rootLayer: null,
        svgLayers: [],
        frameLayers: [],
        imageLayers: [],
        carouselLayers: [],
        videoLayers: []
      } as {
        textLayers: Layer<TextLayerProps>[];
        lineLayers: Layer<LineLayerProps>[];
        shapeLayers: Layer<ShapeLayerProps>[];
        rootLayer: Layer<RootLayerProps> | null;
        svgLayers: Layer<SvgLayerProps>[];
        frameLayers: Layer<FrameLayerProps>[];
        imageLayers: Layer<ImageLayerProps>[];
        carouselLayers: Layer<CarouselLayerProps>[];
        videoLayers: Layer<VideoLayerProps>[];
      }
    );
  }, [activePage, query, selectedLayers]);
  useEffect(() => {
    const layerType: string[] = [];
    if (sidebar && sidebar !== 'LAYER_MANAGEMENT') {
      selectedLayers.forEach((layer) => {
        layerType.push(layer.data.type);
      });
      if (
        sidebar &&
        layerType.includes('Text') &&
        !['FONT_FAMILY', 'TEXT_EFFECT', 'CHOOSING_COLOR', 'ANIMATION_SETTINGS'].includes(sidebar)
      ) {
        actions.setSidebar();
      } else if (
        sidebar &&
        (layerType.includes('Shape') || layerType.includes('Root')) &&
        !['CHOOSING_COLOR', 'IMAGE_MANIPULATION'].includes(sidebar)
      ) {
        actions.setSidebar();
      } else if (
        sidebar &&
        ['IMAGE_MANIPULATION'].includes(sidebar) &&
        !layerType.includes('Frame') &&
        !layerType.includes('Image') &&
        !layerType.includes('Root')
      ) {
        actions.setSidebar();
      } else if (
        sidebar &&
        layerType.includes('Frame') &&
        ['IMAGE_MANIPULATION'].includes(sidebar) &&
        frameLayers.length === 1 &&
        !frameLayers[0].data.props.image
      ) {
        actions.setSidebar();
      } else if (
        sidebar &&
        layerType.includes('Root') &&
        ['IMAGE_MANIPULATION'].includes(sidebar) &&
        !rootLayer.data.props.image
      ) {
        actions.setSidebar();
      } else if (
        sidebar &&
        !layerType.includes('Carousel') &&
        ['CAROUSEL_SETTINGS'].includes(sidebar) &&
        selectedLayers.length !== 1 &&
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        !selectedLayers[0]?.data.props?.itemsCarousel
      ) {
        actions.setSidebar();
      }
    }
    // layer props shouldn't affect this
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebar, selectedLayerIds, actions]);

  return (
    <div
      css={{
        display: 'flex',
        width: '100%',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: 14,
        fontWeight: 600,
        padding: '0 8px',
      }}
    >
      <div
        css={{
          display: 'grid',
          alignItems: 'center',
          gridAutoFlow: 'column',
          gridGap: 8,
          paddingRight: 20,
        }}
      >
        {rootLayer && !isPageLocked && <RootSettings layer={rootLayer} />}
        {/* cannot support editing multiple image */}
        {layerIdList.length === 1 &&
          imageLayers.length === 1 &&
          !isPageLocked && <ImageSettings layers={imageLayers} />}
        {frameLayers.length > 0 && !isPageLocked && (
          <FrameSettings layers={frameLayers} />
        )}
        {lineLayers.length > 0 && !isPageLocked && (
          <LineSettings layers={lineLayers} />
        )}
        {/* TODO: too complex */}
        {layerIdList.length === shapeLayers.length &&
          shapeLayers.length > 0 &&
          !isPageLocked && <ShapeSettings layers={shapeLayers} />}
        {/* TODO: too complex */}
        {layerIdList.length === textLayers.length &&
          textLayers.length > 0 &&
          !isPageLocked && <TextSettings layers={textLayers} />}
        {svgLayers.length === 1 && !isPageLocked && (
          <SvgSettings layer={svgLayers[0]} />
        )}
        <CommonSettings />
        {((selectedLayers.length===1 && !isPageLocked) || (selectedLayers.length===1 && isPremium)) && (
          <HyperlinkSettings/>
        )}
        {(selectedLayers.length===1 && (isPremium || (!isPageLocked && selectedLayerIds[0] === 'ROOT'))) && (
          <ImpressionTagSettings/>
        )}
        {((selectedLayers.length===1 && videoLayers.length > 0 && !isPageLocked)
          || (selectedLayers.length===1 && videoLayers.length > 0 && isPremium)) && (
          <VideoSettings/>
        )}
        {(carouselLayers.length === 1 && !isPageLocked) && (
          <CarouselSettings />
        )}
        {(selectedLayers.length === 1 && !isPageLocked && !rootLayer) && (
          <AnimationSettings />
        )}
        {(selectedLayers.length > 0 && isPremium) && <PremiumSettings/>}
      </div>
    </div>
  );
};

export default LayerSettings;
